import { PayoutListFilter } from '@frontend/api-payouts';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';

type PayoutsState = {
  filter: PayoutListFilter;
  numRows: number;
  stringifiedFilter: string;
  currentPageIndex: number;
};

type PayoutActions = {
  setFilter: (filter: PayoutsState['filter']) => void;
  setNumRows: (numRows: PayoutsState['numRows']) => void;
  setCurrentPageIndex: (currentPageIndex: PayoutsState['currentPageIndex']) => void;
};

type PayoutStore = PayoutsState & PayoutActions;

type Value = string;
type Operator = '<' | '>' | '<=' | '>=' | '=';
type FilterArray<T extends Record<string, any>> = `${Exclude<keyof T, symbol>} ${Operator} ${Value}`[];

type PayoutFilters = {
  estimated_arrival_time: string;
  amount: string;
  status: string;
  payout_descriptor: string;
};

/**
 * Transforms a given input filter object into the FrontendPayoutListFilter structure.
 * This new structure is then suitable for the `convertFilterToString` function.
 *
 * We are sending a parsed string to the backend in order to fit the filtering spec for google's AIP
 * https://google.aip.dev/160
 *
 * This transform function is a WIP. The idea is that in the future, we won't need to do the manual parsing.
 * Instead, it will be generated code that we can import from schema-gen-ts.
 *
 * This is a guinea pig for the new filter parsing system that we are working on.
 * We will update it as more features from schema become available, so that it will be
 * the prime example of how to use the new filter parsing system.
 *
 *
 * @param input The raw filter data object from the frontend.
 * @returns A `Partial<FrontendPayoutListFilter>` object.
 */
function transformFeFilterToQueryString(filter?: Partial<PayoutListFilter> | null): string {
  if (!filter || Object.keys(filter).length === 0) {
    return '';
  }

  const conditions: FilterArray<PayoutFilters> = [];

  if (filter.amount) {
    if (typeof filter.amount.gt === 'number') {
      conditions.push(`amount > ${filter.amount.gt}`);
    }
    if (typeof filter.amount.lt === 'number') {
      conditions.push(`amount < ${filter.amount.lt}`);
    }
    if (typeof filter.amount.eq === 'number') {
      conditions.push(`amount = ${filter.amount.eq}`);
    }
  }

  if (filter.arrival) {
    if (typeof filter.arrival.gte === 'string') {
      conditions.push(`estimated_arrival_time >= '${filter.arrival.gte}'`);
    }
    if (typeof filter.arrival.lte === 'string') {
      conditions.push(`estimated_arrival_time <= '${filter.arrival.lte}'`);
    }
  }
  if (filter.status) {
    if (typeof filter.status === 'string') {
      const escapedValue = filter.status.toLowerCase().replace(/'/g, "\\'");
      conditions.push(`status = '${escapedValue}'`);
    }
  }
  if (filter.descriptor) {
    if (typeof filter.descriptor === 'string') {
      const escapedValue = filter.descriptor.replace(/'/g, "\\'");
      conditions.push(`payout_descriptor = '${escapedValue}'`);
    }
  }

  return conditions.join(' AND ');
}

const DEFAULT_NUM_ROWS = 10;
export const usePayoutStore = createStoreWithSubscribe<PayoutStore>(
  (set) => {
    return {
      filter: {},
      /**
       * @property {string} stringifiedFilter - Stringified JSON of filters for backend communication.
       *
       * @description
       * - Backend requires filters as a string.
       * - Frontend uses a structured JSON object for filters (managed elsewhere, e.g., via `setFilter`).
       * - This `stringifiedFilter` is automatically updated internally when the primary filter object changes.
       * - It's read-only from a component perspective; no direct `setStringifiedFilter` method exists
       * because the string form isn't directly usable by the frontend without parsing.
       * - Components read this property to send filters to the backend.
       */
      stringifiedFilter: '',
      numRows: DEFAULT_NUM_ROWS,
      currentPageIndex: 0,

      setFilter: (newFilter) =>
        set((prevState) => ({
          ...prevState,
          filter: newFilter,
          stringifiedFilter: transformFeFilterToQueryString(newFilter),
        })),
      setNumRows: (numRows) => set((prevState) => ({ ...prevState, numRows })),
      setCurrentPageIndex: (currentPageIndex) => set((prevState) => ({ ...prevState, currentPageIndex })),
    };
  },
  { name: 'PayoutStore' }
);

export const usePayoutShallowStore = createShallowStore(usePayoutStore);
